// File: OrderItemsList.js

import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import Modal from '../../shared/components/ui/Modal';
import Button from '../../shared/components/ui/Button';

import {
  getInvoiceByDealId,
  downloadInvoiceForPaymentStages,
  cancelInvoice,
  getCheque,
  addCheque,
  updateInvoiceWaveOff
} from '../services/api/implementation/impl';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import { XCircle } from 'lucide-react';
import Loader from '../../shared/components/ui/Loader';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import TabComponents from '../../shared/components/TabComponent';
import cancelDealIcon from '../../shared/assets/svg/cancelDeal.svg';
import SalesPayment from './SalesPayment';
import AddChequeForm from '../components/forms/AddChequeForm';
import ChequeTable from '../components/forms/ChequeTable';
import PaymentForm from '../components/forms/PaymentForm';
import ClaimExtraChargesForm from '../components/forms/ClaimExtraChargesForm';

const InvoiceDetails = () => {
  const userId = Cookies.get('userId');
  const { collapsed } = useSidebar();
  const { id } = useParams();
  const [sortDirection, setSortDirection] = useState('none');
  const [activeTab, setActiveTab] = useState('Invoice');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCancelInvoiceModal, setShowCancelInvoiceModal] = useState(false);
  const [invoiceToCancel, setInvoiceToCancel] = useState(null);
  const [showWaveOffModal, setShowWaveOffModal] = useState(false);
  const [invoiceToWaveOff, setInvoiceToWaveOff] = useState(null);

  // Payment modal state
  const [isPaid, setIsPaid] = useState(false);
  const [StatusId, setStatusId] = useState();
  const [dueAmount, setDueAmount] = useState(0);
  const [commonPay, setCommonPay] = useState(false);

  // Cheque modal state
  const [showChequeModal, setShowChequeModal] = useState(false);
  const [chequeData, setChequeData] = useState([]);

  // Claim Extra Charges modal state
  const [showClaimExtraChargesModal, setShowClaimExtraChargesModal] = useState(false);

  // Fetch order activities by order ID
  const {
    data: invoiceData,
    refetch: refetchInvoiceData,
    isLoading
  } = useQuery({
    queryKey: ['orderActivities', id, sortDirection],
    queryFn: () => getInvoiceByDealId(id, 1, 1000000, sortDirection),
    enabled: !!id // Ensures the query runs only when `id` is available
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Order Activity List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  const { mutate: handleInvoicePaymentStagesDownload } = useMutation({
    mutationFn: async ({ id, sourceName }) => {
      return await downloadInvoiceForPaymentStages(id, sourceName);
    },
    onSuccess: (data, variables) => {
      const { id, sourceName } = variables;
      if (!data || data.size === 0) {
        toast.error('Error: File data is empty or invalid.');
        return;
      }
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `Payment_Stage_${id}_${sourceName}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice Downloaded');
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Error in downloading invoice');
    }
  });

  const sanitizeToNumber = (val) => {
    if (val === null || val === undefined) return NaN;
    const s = String(val)
      .replace(/[^0-9.-]/g, '')
      .trim();
    const n = Number(s);
    return Number.isFinite(n) ? n : NaN;
  };

  // Function to fetch cheques
  const fetchCheques = async () => {
    try {
      const dealId = invoiceData?.data?.[0]?.closedDeal?.id;
      if (dealId) {
        const response = await getCheque(dealId);
        console.log('cheque data', response);
        setChequeData(response || []);
      }
    } catch (error) {
      console.error('Error fetching cheques:', error);
      setChequeData([]);
    }
  };

  const { mutate: handleCreateCheques, isPending: isCreatingCheques } = useMutation({
    mutationFn: (payload) => {
      return addCheque(payload);
    },
    onSuccess: () => {
      toast.success('Cheques Created Successfully');
      setShowChequeModal(false);
      // Fetch cheques after successful creation
      fetchCheques();
      // Refetch the data
      refetchInvoiceData();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Error creating cheques');
    }
  });

  const handleSortByDueAmount = (direction) => {
    if (sortDirection === direction) {
      // If clicking on the same direction, toggle it off
      setSortDirection('none');
    } else {
      // Set the new direction
      setSortDirection(direction);
    }
  };

  // Reset payments on modal close
  const resetPaidModal = () => {
    setIsPaid(false);
    setCommonPay(false);
  };

  // Handle claim extra charges success
  const handleClaimExtraChargesSuccess = (payload) => {
    console.log('Claim Extra Charges submitted:', payload);
    refetchInvoiceData();
    setShowClaimExtraChargesModal(false);
  };

  // Cheque handling functions
  const handleChequeSubmit = (payload) => {
    handleCreateCheques(payload);
  };

  const handleViewCheque = (cheque) => {
    // TODO: Implement view cheque details functionality
    console.log('View cheque:', cheque);
  };

  const handleDeleteCheque = (cheque) => {
    // TODO: Implement delete cheque functionality
    console.log('Delete cheque:', cheque);
  };

  const handleStatus = useCallback(
    (id, due) => {
      setStatusId(id);
      const Data = invoiceData?.data?.filter((item) => item.id === Number(id));
      setDueAmount(due);
      setIsPaid(true);
      setCommonPay(false);
    },
    [invoiceData?.data]
  );

  const openCancelInvoiceModal = (poItem) => {
    setInvoiceToCancel(poItem.id);
    setShowCancelInvoiceModal(true);
  };

  const confirmCancelInvoice = async () => {
    if (!invoiceToCancel) {
      toast.error('Invoice id not found');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await cancelInvoice(invoiceToCancel);
      toast.success('Invoice Cancelled Successfully');
      refetchInvoiceData();
      setIsSubmitting(false);
      setShowCancelInvoiceModal(false);
    } catch (result) {
      toast.error(result?.response?.data?.message || result?.message || 'Error deleting payment');
      setIsSubmitting(false);
    }
  };

  // Wave Off handler
  const handleWaveOff = (invoiceId) => {
    setInvoiceToWaveOff(invoiceId);
    setShowWaveOffModal(true);
  };

  const confirmWaveOff = async () => {
    if (!invoiceToWaveOff) {
      toast.error('Invoice id not found');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = { cancelled: true };
      const res = await updateInvoiceWaveOff(invoiceToWaveOff, payload);
      toast.success('Invoice waved off successfully');
      refetchInvoiceData();
      setIsSubmitting(false);
      setShowWaveOffModal(false);
      setInvoiceToWaveOff(null);
    } catch (result) {
      toast.error(result?.response?.data?.message || result?.message || 'Error waving off invoice');
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    refetchInvoiceData();
  }, [id]);

  // Fetch cheques when invoice data is available
  useEffect(() => {
    if (invoiceData?.data?.[0]?.closedDeal?.id) {
      fetchCheques();
    }
  }, [invoiceData?.data?.[0]?.closedDeal?.id]);

  return (
    <>
      <Layout>
        <Loader isVisible={isLoading} />

        {/* Header */}
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <Header data={{ name: 'Invoice Details' }} backArrow={true} />
        </div>

        <div
          className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''} mt-[5rem]`}>
          <GridWrapper width={100}>
            {/* Customer and Deal Information */}
            {invoiceData?.data && invoiceData.data.length > 0 && (
              <>
                <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Customer Information */}
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      Customer Information
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Name:</span>
                        <span className="text-sm font-medium text-gray-800">
                          {invoiceData.data[0]?.closedDeal?.lead?.user?.name || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Phone:</span>
                        <span className="text-sm font-medium text-gray-800">
                          {invoiceData.data[0]?.closedDeal?.lead?.user?.phone_number || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Email:</span>
                        <span className="text-sm font-medium text-gray-800">
                          {invoiceData.data[0]?.closedDeal?.lead?.user?.email || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Status:</span>
                        <span
                          className={`text-sm font-medium px-2 py-1 rounded-full ${
                            invoiceData.data[0]?.closedDeal?.lead?.user?.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                          {invoiceData.data[0]?.closedDeal?.lead?.user?.is_active
                            ? 'Active'
                            : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Close Deal Information */}
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      Close Deal Information
                      <div className="ml-auto flex items-center gap-2">
                        <span
                          className={`text-sm font-medium px-3 py-1 rounded-full ${
                            invoiceData.data[0]?.closedDeal?.status === 'Approved'
                              ? 'bg-green-100 text-green-800 border border-green-300'
                              : invoiceData.data[0]?.closedDeal?.status === 'Cancelled' ||
                                  invoiceData.data[0]?.closedDeal?.status === 'Rejected'
                                ? 'bg-red-100 text-red-800 border border-red-300'
                                : 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                          }`}>
                          {invoiceData.data[0]?.closedDeal?.status || 'N/A'}
                        </span>
                        {invoiceData.data[0]?.closedDeal?.status === 'Cancelled' && (
                          <div className="ml-auto">
                            <span
                              className={`text-sm font-medium px-3 py-1 rounded-full bg-green-100 text-green-800 border border-green-300`}>
                              Final Payable:{' '}
                              {`₹${new Intl.NumberFormat('en-IN').format(
                                invoiceData.data[0]?.closedDeal?.finalPayable || 0
                              )}`}
                            </span>
                          </div>
                        )}
                      </div>
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Unit Name:</span>
                          <span className="text-sm font-medium text-gray-800">
                            {invoiceData.data[0]?.closedDeal?.unit?.unitName || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Block:</span>
                          <span className="text-sm font-medium text-gray-800">
                            {invoiceData.data[0]?.closedDeal?.unit?.block?.name || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Accommodation:</span>
                          <span className="text-sm font-medium text-gray-800">
                            {invoiceData.data[0]?.closedDeal?.unit?.accommodation?.name || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Project:</span>
                          <span className="text-sm font-medium text-gray-800">
                            {invoiceData.data[0]?.closedDeal?.unit?.project?.name || 'N/A'}
                          </span>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Total Price:</span>
                          <span className="text-sm font-medium text-gray-800">
                            ₹
                            {new Intl.NumberFormat('en-IN').format(
                              invoiceData.data[0]?.closedDeal?.total_price || 0
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Paid Amount:</span>
                          <span className="text-sm font-medium text-gray-800">
                            ₹
                            {new Intl.NumberFormat('en-IN').format(
                              invoiceData.data?.reduce((total, invoice) => {
                                return total + (Number(invoice?.paid_amount) || 0);
                              }, 0) || 0
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Due Amount:</span>
                          <span className="text-sm font-medium text-gray-800">
                            ₹
                            {new Intl.NumberFormat('en-IN').format(
                              invoiceData.data[0]?.closedDeal?.due_price || 0
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Deal Closed:</span>
                          <span className="text-sm font-medium text-gray-800">
                            {invoiceData.data[0]?.closedDeal?.closed_at
                              ? new Date(
                                  invoiceData.data[0]?.closedDeal?.closed_at
                                ).toLocaleDateString('en-IN')
                              : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Additional Deal Details */}
                    {invoiceData.data[0]?.closedDeal?.emd_percentage && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Payment Details</h4>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">EMD %:</span>
                            <span className="text-sm font-medium text-gray-800">
                              {invoiceData.data[0]?.closedDeal?.emd_percentage || 0}%
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">EMD Amount:</span>
                            <span className="text-sm font-medium text-gray-800">
                              ₹
                              {new Intl.NumberFormat('en-IN').format(
                                invoiceData.data[0]?.closedDeal?.emd || 0
                              )}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Late Fees:</span>
                            <span className="text-sm font-medium text-gray-800">
                              ₹
                              {new Intl.NumberFormat('en-IN').format(
                                invoiceData.data[0]?.closedDeal?.totalLateFees || 0
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            <div className="flex justify-between items-center mb-6">
              <div className="flex gap-2">
                <button
                  onClick={() => setActiveTab('Invoice')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'Invoice'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}>
                  Invoice
                </button>
                <button
                  onClick={() => setActiveTab('Payments')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'Payments'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}>
                  Payments
                </button>
                <button
                  onClick={() => setActiveTab('Cheque')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'Cheque'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}>
                  Cheques
                </button>{' '}
              </div>
              {invoiceData?.data && invoiceData.data.length > 0 && (
                <div className="flex justify-between items-center gap-2">
                  {invoiceData?.data[0]?.source !== 'Extra Charge' && (
                    <Button
                      onClick={() => setShowClaimExtraChargesModal(true)}
                      displayType="secondary"
                      className="bg-orange-600 hover:bg-orange-700 text-white">
                      Claim Extra Charges
                    </Button>
                  )}
                  <div className="flex gap-2">
                    {activeTab === 'Cheque' && (
                      <Button onClick={() => setShowChequeModal(true)}>Add Cheque</Button>
                    )}

                    {activeTab === 'Invoice' && (
                      <Button
                        onClick={() => {
                          setStatusId(invoiceData?.data[0]?.deal_id);
                          // Sum of dues and late fees across all invoices
                          const totalCommonDue = (invoiceData?.data || []).reduce((sum, poItem) => {
                            const paidAmount = Number(poItem?.paid_amount || 0);
                            const totalAmount =
                              Number(poItem?.amount || 0) + Number(poItem?.prev_amount || 0);
                            const due = totalAmount - paidAmount;
                            const lateFee = Number(poItem?.late_fees || 0);
                            return sum + Math.max(0, due) + lateFee;
                          }, 0);
                          setDueAmount(totalCommonDue);
                          setIsPaid(true);
                          setCommonPay(true);
                        }}>
                        Pay
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {activeTab === 'Invoice' && (
              <div className="relative">
                <div className="overflow-x-auto hide-scrollbar w-full relative rounded-t-lg">
                  <table className="text-left w-full min-w-[1200px]">
                    <thead
                      className="bg-gray-200 !text-gray-600 w-full"
                      style={{ position: 'sticky', top: 0, left: 0, zIndex: 1 }}>
                      <tr className="font-normal h-[3rem] w-full whitespace-nowrap">
                        <th className="px-4 py-2 border-b font-normal cursor-pointer">
                          Invoice Number
                        </th>
                        <th className="px-4 py-2 border-b font-normal">Source</th>
                        <th className="px-4 py-2 border-b font-normal cursor-pointer">
                          Invoice Amount
                        </th>
                        <th className="px-4 py-2 border-b font-normal">Paid Amount</th>
                        <th className="px-4 py-2 border-b font-normal relative">
                          <div className="flex items-center justify-start gap-3">
                            <span>Due Amount</span>
                            <div className="ml-1 flex flex-col items-center justify-center">
                              <button
                                className={`w-4 h-4 ${
                                  sortDirection === 'asc' ? 'text-blue-600' : 'text-gray-400'
                                }`}
                                onClick={() => handleSortByDueAmount('asc')}>
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M5 12l5-5 5 5H5z" />
                                </svg>
                              </button>
                              <button
                                className={`w-4 h-4 -mt-1 ${
                                  sortDirection === 'desc' ? 'text-blue-600' : 'text-gray-400'
                                }`}
                                onClick={() => handleSortByDueAmount('desc')}>
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M15 8l-5 5-5-5h10z" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </th>
                        <th className="px-4 py-2 border-b font-normal">Late Fee</th>
                        <th className="px-4 py-2 border-b font-normal">Status</th>
                        <th className="px-4 py-2 border-b font-normal">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceData?.data && invoiceData?.data?.length > 0 ? (
                        invoiceData?.data?.map((poItem, index) => {
                          const paidAmount = poItem?.paid_amount || 0;
                          const dueAmount = Number(poItem?.amount) - Number(paidAmount);
                          const lateFee = poItem?.late_fees || 0;

                          return (
                            <React.Fragment key={poItem?.id || index}>
                              <tr className="border-b hover:bg-gray-50 bg-white h-[3.5rem]">
                                <td className="px-4 py-2 text-sm">
                                  {poItem?.invoiceNumber || '---'}
                                </td>
                                <td className="px-4 py-2 text-sm">{poItem?.source || '---'}</td>{' '}
                                <td className="px-4 py-2 text-sm">
                                  ₹{new Intl.NumberFormat('en-IN').format(poItem?.amount)}
                                </td>
                                <td className="px-4 py-2 text-sm">
                                  ₹{new Intl.NumberFormat('en-IN').format(paidAmount)}
                                </td>
                                <td className="px-4 py-2 text-sm">
                                  ₹{new Intl.NumberFormat('en-IN').format(dueAmount)}
                                </td>
                                <td className="px-4 py-2 text-sm">
                                  ₹{new Intl.NumberFormat('en-IN').format(lateFee)}
                                </td>
                                <td className="px-4 py-2 text-sm">
                                  <div className="relative z-0">
                                    <span className="flex pl-6 overflow-hidden text-sm">
                                      {poItem?.status || '---'}
                                    </span>
                                    <div
                                      className={`w-[5px] h-[100%] rounded-t-2xl rounded-r-[3rem] absolute left-[4px] top-[-2px] ${
                                        ['unpaid', 'Unpaid'].includes(poItem?.status)
                                          ? 'bg-[#C2281B]' // Red for unpaid/unapproved
                                          : ['paid', 'Paid'].includes(poItem?.status)
                                            ? 'bg-[#3F8D78]' // Green for paid/approved
                                            : [
                                                  'pending',
                                                  'Pending',
                                                  'Ongoing',
                                                  'invite-sent',
                                                  'Not Completed',
                                                  'Material Dispatch',
                                                  'Available',
                                                  'Medium',
                                                  'Confirm Delivery'
                                                ].includes(poItem?.status)
                                              ? 'bg-[#E97B37]' // Orange for pending/medium
                                              : 'bg-gray-400' // Default gray
                                      }`}>
                                      &nbsp;
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-2">
                                  <div className="flex items-center justifycenter w-full relative gap-2">
                                    {/* Download button - only show if status is not "paid" */}

                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleInvoicePaymentStagesDownload({
                                          id: poItem.deal_id,
                                          sourceName: poItem.source
                                        });
                                      }}
                                      className="h-9 w-9 grid place-items-center bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors duration-200"
                                      title="Download">
                                      <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                        />
                                      </svg>
                                    </button>

                                    {/* Payment button - only show if status is not "paid" */}
                                    {poItem?.status !== 'paid' &&
                                      poItem?.status !== 'Paid' &&
                                      poItem?.status !== 'Cancelled' &&
                                      !poItem.cancelled && (
                                        <>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const totalAmount =
                                                Number(dueAmount) + Number(lateFee);
                                              handleStatus(poItem.id, totalAmount);
                                            }}
                                            className="h-9 px-3 inline-flex items-center justify-center bg-green-600 hover:bg-green-700 text-white rounded-md text-sm gap-1 transition-colors duration-200"
                                            title="Pay">
                                            <span className="text-base">₹</span>
                                            <span>Pay</span>
                                          </button>

                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleWaveOff(poItem.id);
                                            }}
                                            className="h-9 w-9 inline-flex items-center justify-center bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors duration-200"
                                            title="Wave Off">
                                            <XCircle size={16} />
                                          </button>
                                        </>
                                      )}

                                    {/* <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openCancelInvoiceModal(poItem);
                                      }}
                                      className="h-9 px-3 inline-flex items-center justify-center gap-1 bg-red-50 hover:bg-red-100 text-red-700 rounded-md transition-colors duration-200 border border-red-200"
                                      title="Cancel Invoice">
                                      <img
                                        src={cancelDealIcon}
                                        alt="cancel_deal"
                                        className="w-4 h-4"
                                      />
                                      Cancel
                                    </button> */}
                                  </div>
                                </td>
                              </tr>
                            </React.Fragment>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center py-10 align-middle">
                            <div className="flex flex-col items-center justify-center w-full h-full">
                              <h2 className="text-lg font-bold text-gray-800 mb-1">
                                No Data Found
                              </h2>
                              <p className="text-gray-600 text-sm">No invoice data available.</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'Payments' && <SalesPayment chequeData={chequeData} dealID={id} />}

            {activeTab === 'Cheque' && (
              <ChequeTable
                chequeData={chequeData}
                onAddCheque={() => setShowChequeModal(true)}
                onViewCheque={handleViewCheque}
                onDeleteCheque={handleDeleteCheque}
              />
            )}
          </GridWrapper>
        </div>
      </Layout>

      {showCancelInvoiceModal && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={'Confirmation'}
          onClose={() => setShowCancelInvoiceModal(false)}
          width={30}>
          <div className="flex flex-col justify-center align-center ">
            <p className="text-center">Are you sure you want to cancel this invoice?</p>
          </div>
          <div className="w-full flex justify-end items-center space-x-4 mt-8">
            <Button
              type="button"
              displayType="secondary"
              onClick={() => setShowCancelInvoiceModal(false)}>
              No
            </Button>
            <Button type="button" disabled={isSubmitting} onClick={confirmCancelInvoice}>
              Yes
            </Button>
          </div>
        </Modal>
      )}

      {/* Wave Off Confirmation Modal */}
      {showWaveOffModal && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={'Wave Off Confirmation'}
          onClose={() => setShowWaveOffModal(false)}
          width={30}>
          <div className="flex flex-col justify-center align-center">
            <p className="text-center">Are you sure you want to wave off this invoice?</p>
            <p className="text-center text-sm text-gray-600 mt-2">
              This action will mark the invoice as cancelled.
            </p>
          </div>
          <div className="w-full flex justify-end items-center space-x-4 mt-8">
            <Button
              type="button"
              displayType="secondary"
              onClick={() => setShowWaveOffModal(false)}>
              No
            </Button>
            <Button type="button" disabled={isSubmitting} onClick={confirmWaveOff}>
              Yes
            </Button>
          </div>
        </Modal>
      )}

      <PaymentForm
        isOpen={isPaid}
        onClose={resetPaidModal}
        invoiceData={invoiceData}
        dueAmount={dueAmount}
        commonPay={commonPay}
        statusId={StatusId}
        chequeData={chequeData}
        onSuccess={() => {
          // Refetch the data after successful payment
          refetchInvoiceData();
        }}
      />

      {showChequeModal && (
        <AddChequeForm
          isOpen={showChequeModal}
          onClose={() => setShowChequeModal(false)}
          onSubmit={handleChequeSubmit}
          invoiceData={invoiceData}
          sanitizeToNumber={sanitizeToNumber}
          isSubmitting={isCreatingCheques}
        />
      )}

      {/* Claim Extra Charges Modal */}
      {showClaimExtraChargesModal && (
        <ClaimExtraChargesForm
          isOpen={showClaimExtraChargesModal}
          onClose={() => setShowClaimExtraChargesModal(false)}
          dealId={id}
          onSuccess={handleClaimExtraChargesSuccess}
          projectId={invoiceData.data[0]?.closedDeal?.unit?.project?.id}
        />
      )}
    </>
  );
};

export default InvoiceDetails;
